import type { AxiosHeaders } from 'axios';
import { api } from './index';

const BASE_PATH = '/api/internal/patients';

export type ServicePrice = {
  id: number;
  serviceType: string;
  weekPrice: number;
  weekCost: number;
  weekendPrice: number;
  weekendCost: number;
  withTaxBill: boolean;
  humanServiceType: string;
};

export interface PatientAddressForm {
  id?: number;
  label?: string;
  address?: string;
  latitude?: string | null;
  longitude?: string | null;
  locationExtended?: boolean;
  primary?: boolean;
  _destroy?: boolean;
}

export type PatientAddress = {
  id: number;
  label: string | null;
  address: string | null;
  latitude: string | null;
  longitude: string | null;
  locationExtended: boolean;
  primary: boolean;
}

export type CurrentServiceAssignment = {
  id: number;
  categoryId: number;
  categoryName: string;
  categoryPrioritizationEnabled: boolean;
  subcategoryId: number | null;
  subcategoryName: string | null;
  startsOn: string;
};

// Sent alongside the patient payload only when the category selection changed.
export type ServiceAssignmentPayload = {
  categoryId: number | null;
  subcategoryId?: number | null;
  startsOn?: string;
};

// Patient currency as computed by Patients::StatusState (only for companies with
// care planning; null otherwise). Buckets map to the list tabs; the hint explains why —
// exactly what the Estado chip renders, nothing more rides the wire.
export type PatientStatusBucket = 'active' | 'inactive' | 'discarded';
export type PatientStatusHint =
  | 'requirement_open' | 'requirement_future' | 'shift_scheduled' | 'service_until'
  | 'requirement_ended' | 'no_recent_shift' | 'never_served';

export type PatientStatus = {
  bucket: PatientStatusBucket;
  hint: PatientStatusHint | null;
  hintDate: string | null;
  inactiveDays: number | null;
  lastShiftAt: string | null;
};

// Fixed catalog (Patient.discard_reasons); labels live in the es locale.
export const DISCARD_REASONS = [
  'goals_met',
  'deceased',
  'hospitalized_or_institutionalized',
  'changed_provider',
  'family_decision',
  'economic_or_coverage',
  'moved_out_of_coverage',
  'created_by_mistake',
  'other',
] as const;
export type DiscardReason = typeof DISCARD_REASONS[number];

export type DiscardPayload = {
  discardReason?: DiscardReason | null;
  discardNote?: string | null;
  endsOn?: string | null;
};

// Archive details shared by the list item and the full patient.
export type PatientDiscardDetails = {
  discardedAt: string | null;
  discardReason: DiscardReason | null;
  humanDiscardReason: string | null;
  discardNote: string | null;
  discardedByName: string | null;
};

// Pointer returned with the 400 when a new patient's RUT belongs to an archived one.
export type ArchivedPatientConflict = {
  id: number;
  fullName: string;
  discardedAt: string;
  humanDiscardReason: string | null;
};

export type Patient = PatientDiscardDetails & {
  // Representatives who receive the billing emails (ticked, or the first one while none is).
  billingRepresentativeIds?: number[];
  id: number;
  idCode: number;
  firstName: string;
  lastName: string;
  nickname: string;
  fullName: string;
  dni: string;
  passportNumber?: string;
  address: string | null;
  latitude: string | null;
  longitude: string | null;
  patientAddressesAttributes: PatientAddress[];
  weight: number;
  birthdate: Date;
  humanBirthdate: string;
  age?: number;
  sex: string;
  humanSex: string;
  medicalHistory: string;
  surgicalHistory: string;
  technicalAids: string;
  insurances: string;
  // The antecedentes changed after they were first recorded; absent without
  // the permission to read them.
  antecedentesEdited?: boolean;
  comments: string;
  startsOn?: string;
  endsOn?: string;
  humanStartsOn?: string;
  humanEndsOn?: string;
  servicePrices: ServicePrice[];
  groupIds?: number[];
  groups?: Array<{ id: number; name: string }>;
  currentServiceAssignment?: CurrentServiceAssignment | null;
  status?: PatientStatus | null;
  representativesAttributes: [
    {
      id: number;
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber: string;
      dni: string;
    }
  ],
  representative: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    dni: string;
  }
}

// The index (no for_list/for_filters) leaves out the antecedentes: lists carry no
// clinical data, which comes with one patient's show.
export type PatientWithoutAntecedentes = Omit<
  Patient,
  'medicalHistory' | 'surgicalHistory' | 'technicalAids' | 'insurances' | 'antecedentesEdited'
>;

export interface PatientForm {
  billingRepresentativeIds?: number[];
  id?: number;
  idCode?: number;
  firstName: string;
  lastName: string;
  nickname: string;
  dni: string;
  passportNumber?: string;
  weight: number;
  birthdate: Date;
  sex: string;
  medicalHistory: string;
  surgicalHistory: string;
  technicalAids: string;
  insurances: string;
  comments: string;
  startsOn?: string;
  endsOn?: string;
  representativesAttributes: [
    {
      id?: number;
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber: string;
      dni: string;
    }
  ]
  patientAddressesAttributes: PatientAddressForm[];
}

export interface PDFPatient {
  headers: AxiosHeaders;
  data: Blob;
}

export interface XlsxPatients {
  headers: AxiosHeaders;
  data: Blob;
}

export type PatientFilter = {
  id: number;
  fullName: string;
  dni: string | null;
  groupIds?: number[];
}

// Slim shape returned by the patients index list view (for_list=true). Only the
// fields the list table/search and its discard modals use — not the full Patient
// payload (see Api::Internal::PatientListSerializer).
export type PatientListItem = PatientDiscardDetails & {
  id: number;
  idCode: number;
  firstName: string;
  lastName: string;
  fullName: string;
  dni: string;
  serviceCategoryId: number | null;
  serviceCategoryName: string | null;
  serviceSubcategoryName: string | null;
  endsOn: string | null;
  status: PatientStatus | null;
  representative: {
    fullName: string;
    phoneNumber: string;
  };
}

export type PatientForGroupsManagement = {
  id: number;
  firstName: string;
  lastName: string;
  fullName: string;
  dni: string;
  idCode?: number;
  groupIds: number[];
  groups: Array<{ id: number; name: string }>;
}

function idArrayParams(key: string, ids?: number[]): string {
  if (!ids || ids.length === 0) return '';

  return ids.map(id => `&${key}[]=${id}`).join('');
}

export const patientsApi = {
  index(
    scope?: string,
    groupIdIn?: number[],
    ungrouped?: boolean,
  ): Promise<Array<PatientWithoutAntecedentes>> {
    const params: Record<string, unknown> = {};
    if (scope) params.scope = scope;
    if (groupIdIn && groupIdIn.length > 0) params['group_id_in[]'] = groupIdIn;
    if (ungrouped) params.ungrouped = true;

    return api({
      method: 'get',
      url: BASE_PATH,
      params,
    }).then(response => response.data.patients);
  },
  indexForList(
    scope?: string,
    groupIdIn?: number[],
    serviceCategoryIdIn?: number[],
    uncategorized?: boolean,
  ): Promise<Array<PatientListItem>> {
    const scopeParam = scope ? `&scope=${scope}` : '';
    const url = `${BASE_PATH}?for_list=true${scopeParam}` +
      `${idArrayParams('group_id_in', groupIdIn)}` +
      `${idArrayParams('service_category_id_in', serviceCategoryIdIn)}` +
      `${uncategorized ? '&uncategorized=true' : ''}`;

    return api({ method: 'get', url }).then(response => response.data.patients);
  },
  indexForFilters(
    groupIdIn?: number[],
    options?: { serviceCategoryIdIn?: number[]; uncategorized?: boolean },
  ): Promise<Array<PatientFilter>> {
    const params = [
      'for_filters=true',
      ...(groupIdIn ?? []).map(id => `group_id_in[]=${id}`),
      ...(options?.serviceCategoryIdIn ?? []).map(id => `service_category_id_in[]=${id}`),
      ...(options?.uncategorized ? ['uncategorized=true'] : []),
    ];

    return api({ method: 'get', url: `${BASE_PATH}?${params.join('&')}` })
      .then(response => response.data.patients);
  },
  indexForGroupsManagement(
    groupIdIn?: number[],
    ungrouped?: boolean,
  ): Promise<Array<PatientForGroupsManagement>> {
    let url = `${BASE_PATH}?for_groups_management=true`;
    if (groupIdIn && groupIdIn.length > 0) {
      url += groupIdIn.map(id => `&group_id_in[]=${id}`).join('');
    }
    if (ungrouped) url += '&ungrouped=true';

    return api({ method: 'get', url }).then(response => response.data.patients);
  },
  indexAsXlsx(scope: string, options: {
    groupIds?: number[];
    serviceCategoryIdIn?: number[];
    uncategorized?: boolean;
    status?: PatientStatusBucket;
  } = {}): Promise<XlsxPatients> {
    const params = [
      `scope=${scope}`,
      ...(options.groupIds ?? []).map(id => `group_id_in[]=${id}`),
      ...(options.serviceCategoryIdIn ?? []).map(id => `service_category_id_in[]=${id}`),
      ...(options.uncategorized ? ['uncategorized=true'] : []),
      ...(options.status ? [`status=${options.status}`] : []),
    ];

    return api({
      method: 'get',
      url: `${BASE_PATH}.xlsx?${params.join('&')}`,
      responseType: 'blob',
    });
  },
  showAsPdf(id: number): Promise<PDFPatient> {
    return api({
      method: 'get',
      url: `${BASE_PATH}/${id}.pdf`,
      responseType: 'blob',
    });
  },
  create(
    patientForm: PatientForm,
    groupIds?: number[],
    serviceAssignment?: ServiceAssignmentPayload,
  ): Promise<Patient> {
    const data: Record<string, unknown> = { patient: patientForm };
    if (groupIds !== undefined) data.groupIds = groupIds;
    if (serviceAssignment !== undefined) data.serviceAssignment = serviceAssignment;

    return api({
      method: 'post',
      url: BASE_PATH,
      data,
    }).then(response => response.data.patient);
  },
  update(
    patientForm: Partial<PatientForm>,
    groupIds?: number[],
    serviceAssignment?: ServiceAssignmentPayload,
  ): Promise<Patient> {
    const data: Record<string, unknown> = { patient: patientForm };
    if (groupIds !== undefined) data.groupIds = groupIds;
    if (serviceAssignment !== undefined) data.serviceAssignment = serviceAssignment;

    return api({
      method: 'put',
      url: `${BASE_PATH}/${patientForm.id}`,
      data,
    }).then(response => response.data.patient);
  },
  updateGroups(id: number, groupIds: number[]): Promise<void> {
    return api({
      method: 'put',
      url: `${BASE_PATH}/${id}/group_memberships`,
      data: { groupIds },
    }).then(() => {});
  },
  // Archives (discards) the patient; reason/note/end date are optional (care-planning
  // companies send them, the legacy dialog sends nothing).
  destroy(id: number, payload: DiscardPayload = {}): Promise<void> {
    return api({
      method: 'delete',
      url: `${BASE_PATH}/${id}`,
      data: payload,
    }).then(() => {});
  },
  // An empty PUT on an archived patient reactivates it (new episode for care-planning
  // companies, plain desarchivar otherwise).
  reactivate(id: number): Promise<Patient> {
    return this.update({ id });
  },
};
